import {
  DONT_RENDER_APP,
  APP_ID_PRISMA_ACCESS_EDITION_ONPREM,
  APP_ID_PRISMA_ACCESS_EDITION,
  APP_ID_PRISMA_ACCESS,
  APP_ID_LOGGING_SERVICE
} from './GlobalConstants'
import { IOT_PACKAGE_1, IOT_PACKAGE_2, IOT_3P_ADDON_APP_ID, IOT_HASH_CONTEXT } from '../../constants'

import type { Licenses, SubLicense } from '../../store'
import type { ManifestFlags, ActivationManifest, ComponentDef } from '../../renderer'
import type { Tsg, ReadonlyTsgTree } from '@sparky/framework'

const isPrimaryLicenseActivated = (licenses: Licenses, primaryAppId: string) => {
  const primaryLicenses = primaryAppId ? licenses?.find(lic => lic.app_id === primaryAppId)?.licenses : []
  return primaryLicenses?.every(lic => Boolean(lic.is_csp_activated && !lic.is_ela))
}

export const filterOutDoNotRenderApp = (apps: any) => apps?.filter((each: any) => !DONT_RENDER_APP.includes(each.app_id))

/**
 * This util will help to transfer all the entitlements to prisma_access from prisma_access_edition
 * @param {*} apps
 * @returns new Array
 */
export const transferLicenseToPA = ((instances: any) => {
  const _instances = [...instances]
  const licenseInstance = _instances?.find((each: any) => each.app_id === APP_ID_PRISMA_ACCESS_EDITION_ONPREM || each.app_id === APP_ID_PRISMA_ACCESS_EDITION) || {}
  const prismaAccess = _instances?.find((each: any) => each.app_id === APP_ID_PRISMA_ACCESS) || {}
  prismaAccess.contract = licenseInstance?.serial_number
  // prismaAccess.url is correct, do not overwrite here
  if (licenseInstance.provisioning_status === 'complete' && licenseInstance.status === 'running' &&
    prismaAccess.provisioning_status === 'complete' && prismaAccess.status === 'running') {
    prismaAccess.provisioning_status = 'complete'
  }
  else if (licenseInstance.provisioning_status === 'error' || prismaAccess.provisioning_status === 'error' ||
    licenseInstance.status === 'error' || prismaAccess.status === 'error') {
    prismaAccess.provisioning_status = 'error'
  }
  else {
    prismaAccess.provisioning_status = prismaAccess.provisioning_status ?? licenseInstance.provisioning_status
  }
  if (OBJECT.hasObject(prismaAccess)) {
    prismaAccess.entitlements = [...licenseInstance.entitlements || []]
  }
  return filterOutDoNotRenderApp(_instances)
})

export const isMagicLinkHashFullyActivated = (data: any) => {
  const { licenses }: { licenses: Licenses } = data
  const hashLicenses = licenses?.reduce((result, each) => {
    if (each?.licenses?.length) {
      result.push(...each.licenses)
    }
    return result
  }, [] as SubLicense[])
  return hashLicenses.every(e => e.is_csp_activated)
}

export const appIdsEligibleForActivation = (licenses: any, primary_app_id: string, flags: ManifestFlags) => {
  const appIdsToActivate = []
  if (flags?.hash_required && flags?.cdl_required) {
    const cdlLicense = licenses?.find((each: any) => each.app_id === APP_ID_LOGGING_SERVICE)?.licenses[0]
    const isCdlLicenseActivated = cdlLicense ? Boolean(cdlLicense?.is_csp_activated) : true
    const isPrimaryAppLicenseActivated = isPrimaryLicenseActivated(licenses, primary_app_id)
    if (!isCdlLicenseActivated) {
      appIdsToActivate.push(APP_ID_LOGGING_SERVICE)
    }
    if (!isPrimaryAppLicenseActivated) {
      appIdsToActivate.push(primary_app_id)
    }
  }
  else {
    appIdsToActivate.push(primary_app_id)
  }
  return appIdsToActivate
}
/***
 * General Object Utils
 */
export const OBJECT = {
  isObject: (object: any) => (object && !Array.isArray(object) && typeof object === 'object'),
  hasObject: (object: any) => !!(OBJECT.isObject(object) && Object.keys(object).length),
  reduce: (object: any) => (OBJECT.hasObject(object) ? Object.fromEntries(Object.entries(object).filter(([_, v]) => v)) : {}),
  omit: (source: any, key: any) => {
    const copy = Object.assign({}, source)
    delete copy[key]
    return copy
  },
  deepFreeze: (obj: any) => {
    Object.keys(obj).forEach(prop => {
      if (typeof obj[prop] === 'object' && !Object.isFrozen(obj[prop])) OBJECT.deepFreeze(obj[prop])
    })
    return Object.freeze(obj)
  }
}
/***
 * General Array Utils
 */
export const ARRAY = {
  hasArray: (array: any) => !!(array && Array.isArray(array) && array.length),
  getLength: (array: any) => (ARRAY.hasArray(array) ? array.length : 0),
  clean: (array: any) => (ARRAY.hasArray(array) ? array.filter(Boolean) : [])
}

export const StringTemplate = (str: string, data: [string]) => {
  return str.replace(/({\d})/g, (i: any) => {
    return data[i.replace(/{/, '').replace(/}/, '')]
  })
}

export const runtimeManifestOverride = ({ manifest, licenses, context }: { manifest: any, licenses: Licenses, context: any }) => {
  const flattenLicenses = licenses?.reduce((result, each) => {
    if (each.licenses?.length) {
      result.push(...each.licenses)
    }
    return result
  }, [] as SubLicense[])

  if (context === IOT_HASH_CONTEXT) {
    const isPrimaryApp = flattenLicenses.some(e => e.app_id === manifest.primary_app_id)
    if (isPrimaryApp) { // ML with IoT licenses
      const IoTPkg = flattenLicenses.some(e => e.license_version === IOT_PACKAGE_1) ? IOT_PACKAGE_1 : IOT_PACKAGE_2
      manifest.activation_screens[0].props.children[2].props.children.push(...manifest?.extra?.[IoTPkg])
    }
    else if (!isPrimaryApp && flattenLicenses.every(e => e.app_id === IOT_3P_ADDON_APP_ID)) { //ML with only IoT 3p license
      manifest.activation_screens[0].props.children[2].props.children = manifest?.extra?.[IOT_3P_ADDON_APP_ID] // overwrite
    }
  }

  return manifest
}

export const isNonemptyArray = (arr: Array<any> | any): arr is NonNullable<Array<any>> => Array.isArray(arr) && arr.length > 0

export const findMostParent = (target: Tsg | undefined, tsgTree: ReadonlyTsgTree): Tsg | undefined => {
  if (!target) {
    return undefined
  }
  let i = target
  while (i.parent_id !== '_superRoot') {
    const tsg = tsgTree.get(i.parent_id || '')
    if (tsg) {
      i = tsg
    }
    else {
      return undefined
    }
  }
  return i
}

export const primaryAppAvailableAddonLicenses = (primaryAppManifest: ActivationManifest, licenses: Licenses): SubLicense[] => {
  const appAddonsManifest = primaryAppManifest?.flags?.addons || []
  const addons = appAddonsManifest?.reduce((result, addonDef) => {
    const addOnLicenses = licenses?.get(addonDef?.app_id)?.licenses
    if (addOnLicenses?.length) {
      addOnLicenses.forEach(eachLicense => {
        if (addonDef.autoActivate && !eachLicense?.license_expiration) { //available for activation
          result.push(...addOnLicenses)
        }
      })
    }
    return result
  }, [] as SubLicense[])

  return addons
}

export const findAccordionComponents = (componentDef: ComponentDef): ComponentDef[] => {
  let accordionComponents: ComponentDef[] = []

  // If the component property is 'activation.UAccordion', add it to the array
  if (componentDef.component === 'activation.UAccordion') {
    accordionComponents.push(componentDef)
  }

  // If there are children, recursively call this function on each child
  if (componentDef.props.children) {
    const children = Array.isArray(componentDef.props.children) ? componentDef.props.children : [componentDef.props.children]
    for (const child of children) {
      accordionComponents = accordionComponents.concat(findAccordionComponents(child))
    }
  }

  return accordionComponents
}

declare type PendoEventProps = {
  name: string; request: any[]; status: number; message: string
}
export const pendoEventTracker = ({ name, request, status, message }: PendoEventProps) => {
  const formattedDateTime = new Date().toISOString().slice(0, -5).replace('T', ' ')
  const pendoTrack = (payload: Object) =>
    window.pendo?.track?.(name, { ...payload, timestamp: formattedDateTime })

  // just in case the list is empty
  if (request.length === 0) {
    pendoTrack({})
    return
  }
  // we have one and only request in the list
  const { hash, xsig, instances, ...attrs } = request[0]
  pendoTrack({ status, message, hash, ...attrs })

  instances.forEach(({ allocation, ...rest }: { allocation: Object[]}, i: number) => {
    pendoTrack({ hash, i, ...rest })

    allocation.forEach((item: Object, j: number) =>
      pendoTrack({ hash, i, j, item }))
  })
}
